// The exam server's API (RFC-056): the organisation's item bank, a service
// beside the relay. The relay says who the person is: it hands a token for
// the exam server, five minutes, to a person let into the bank, with where
// the server is; every call here carries it as a bearer, and none carries a
// cookie. A token is asked for again shortly before it runs out, and once
// more when the server refuses one (a key changed, a place changed).
import { ApiError } from "./api";
import { t, word } from "./i18n";

export type BankRole = "admin" | "user";

/** What the relay says of the person's place in the bank, on /v1/me. */
export interface BankPlace {
  role: BankRole;
  url: string;
}

export interface BankClass {
  id: string;
  name: string;
  join_code: string;
}

/** The person as the exam server knows them. */
export interface BankMe {
  user: { id: string; email: string; name: string; role: BankRole; classes: BankClass[] };
  role: BankRole;
  bank: string;
  settings: { catalog: "metadata" | "public"; hidden_release: boolean };
}

/** A place in the bank, as the relay keeps it. */
export interface BankMember {
  user_id: string;
  email: string;
  name: string;
  role: BankRole;
  share_admin?: boolean;
  granted_by?: string;
  granted_at?: string;
  expires_at?: string;
  live: boolean;
}

/** A row of the bank's audit trail. */
export interface AuditRow {
  id: string;
  actor: string;
  actor_email: string;
  action: string;
  target: string;
  detail: Record<string, unknown>;
  at: string;
}

export interface BankSettings {
  catalog: "metadata" | "public";
  hidden_release: boolean;
  monthly_budget_usd: number;
  claude_credential: string;
  updated_by?: string;
  updated_at?: string;
}

/** What byj says an item measures (RFC-055 `:item`). */
export interface ItemMeasure {
  family?: string;
  competencies?: Record<string, number>;
  difficulty?: { authored?: number; measured?: number; discrimination?: number };
  minutes?: number;
  status?: string;
  "variant-of"?: string;
}

/** What byj says of a kit (exam-relay/meta-of), as much as the person sees. */
export interface ItemMeta {
  id?: string;
  title?: string;
  item?: ItemMeasure;
  questions?: { id: string; mode?: string; prompt?: string; options?: string[]; choose?: number }[];
  instructor?: { hidden?: string[] };
  validation?: { "ok?"?: boolean; problems?: string[] };
}

export type VersionState = "uploading" | "checking" | "invalid" | "in_review" | "changes_requested" | "rejected" | "withdrawn" | "accepted";
export type ItemStatus = "submitted" | "accepted" | "calibrated" | "active" | "retired";

export interface ItemVersion {
  version: number;
  state: VersionState;
  note?: string;
  why?: string;
  kit_hash?: string;
  meta?: ItemMeta;
  validation?: { "ok?"?: boolean; problems?: string[] };
  size?: number;
  created_at: string;
  submitted_at?: string;
  decided_at?: string;
}

export interface ItemReview {
  id: string;
  version: number;
  email: string;
  verdict: "accept" | "reject" | "changes";
  comment: string;
  at: string;
}

/** An item as the person sees it (RFC-056 §3, D4). */
export interface BankItem {
  id: string;
  title: string;
  family: string;
  status: ItemStatus;
  current: number;
  latest: number;
  author_email?: string;
  mine: boolean;
  meta?: ItemMeta;
  latest_state?: VersionState;
  created_at: string;
  updated_at: string;
  versions?: ItemVersion[];
  reviews?: ItemReview[];
  can?: Partial<Record<"review" | "activate" | "calibrate" | "retire" | "withdraw" | "new_version" | "delete" | "hidden" | "public", boolean>>;
  /** How the version drawn did across the finished assignments that sat it (RFC-056 §7). */
  measure?: { n: number; right?: number; discrimination?: number; assignments: number; why?: string[] };
}

export interface BankJob {
  id: string;
  kind: string;
  target: string;
  state: "queued" | "running" | "done" | "failed";
  tries: number;
  log: string;
  error: string;
  spend_usd: number;
  /** What it was given: a grade's or a regrade's paper and hand-in (its target is the hand-in alone). */
  input?: { paper?: string; submission?: string } | null;
  created_at: string;
  started_at?: string;
  finished_at?: string;
}

/** What the server read of a blueprint (RFC-056 §4). */
export interface BlueprintSummary {
  id: string;
  title: string;
  audience?: string;
  minutes?: number;
  items?: number;
  forms?: number;
  approval?: boolean;
  pass_mark?: number;
  whole?: Record<string, unknown>;
}

export interface Blueprint {
  id: string;
  title: string;
  published: number;
  latest: number;
  summary?: BlueprintSummary;
  /** The published version's summary, beside the one shown when they differ: an admin's draft after it (byj-1wv.100). */
  published_summary?: BlueprintSummary;
  edn?: string;
  versions?: number[];
  updated_at: string;
}

export type RequestState = "pending" | "drawing" | "delivered" | "failed" | "refused" | "withdrawn";

export interface PaperRequest {
  id: string;
  requester_email: string;
  blueprint: string;
  blueprint_version: number;
  /** The title of the blueprint version the request recorded, whatever version its blueprint shows now (byj-1wv.101). */
  blueprint_title?: string;
  class_id: string;
  class_name: string;
  class_code: string;
  opens_at?: string;
  closes_at?: string;
  forms: number;
  seed: number;
  note: string;
  state: RequestState;
  why: string;
  created_at: string;
}

export interface Paper {
  id: string;
  request: string;
  form: number;
  title: string;
  items: { item: string; version: number; id: string }[];
  exam: string;
  exam_url?: string;
  class_code: string;
  report: string;
  created_at: string;
}

/** A hand-in to a paper of the bank, its grade on Share and the bank's last job on it (RFC-056 §5). */
export interface GradingRow {
  id: string;
  question: string;
  student: string;
  uploaded_at: string;
  finalized: boolean;
  grade?: { id: string; score: number; override?: number; released: boolean };
  job?: BankJob;
}

export interface PaperGrading {
  submissions: GradingRow[];
  spend_usd: number;
  over_budget: boolean;
  month_spend_usd: number;
  budget_usd: number;
}

type Token = { token: string; url: string; role: BankRole; expires: number };

/** How long before a token runs out it is asked for again. */
const renewBefore = 30_000;

/** The relay's token for the exam server, kept until shortly before it runs out. */
export class BankClient {
  private tok: Token | null = null;
  private asking: Promise<Token> | null = null;
  constructor(private readonly fetcher: typeof fetch = (...a) => fetch(...a)) {}

  private async token(fresh = false): Promise<Token> {
    if (!fresh && this.tok && this.tok.expires - Date.now() > renewBefore) return this.tok;
    this.asking ??= (async () => {
      try {
        const res = await this.fetcher("/v1/exam-server/token", {
          method: "POST",
          credentials: "same-origin",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: "{}",
        });
        const out = await read<{ token: string; url: string; role: BankRole; expires_at: string }>(res);
        this.tok = { token: out.token, url: out.url.replace(/\/+$/, ""), role: out.role, expires: Date.parse(out.expires_at) };
        return this.tok;
      } finally {
        this.asking = null;
      }
    })();
    return this.asking;
  }

  /** One call to the exam server; a 401 asks the relay for a token once more. */
  async call<T>(method: string, path: string, body?: unknown, again = true): Promise<T> {
    const tok = await this.token();
    const init: RequestInit = { method, headers: { Accept: "application/json", Authorization: `Bearer ${tok.token}` } };
    if (body !== undefined) {
      init.body = JSON.stringify(body);
      init.headers = { ...init.headers, "Content-Type": "application/json" };
    }
    const res = await this.fetcher(tok.url + path, init);
    if (res.status === 401 && again) {
      await this.token(true);
      return this.call<T>(method, path, body, false);
    }
    return read<T>(res);
  }

  /** Forget the token: after signing out, or a place changed. */
  forget() {
    this.tok = null;
  }

  me = () => this.call<BankMe>("GET", "/v1/me");
  members = async () => (await this.call<{ members: BankMember[] }>("GET", "/v1/members")).members;
  grantMember = (email: string, expires_at?: string) => this.call<BankMember>("POST", "/v1/members", { email, ...(expires_at ? { expires_at } : {}) });
  endMember = (userId: string) => this.call<void>("DELETE", `/v1/members/${encodeURIComponent(userId)}`);
  audit = async (q: { target?: string; actor?: string } = {}) => {
    const p = new URLSearchParams(Object.entries(q).filter(([, v]) => v) as [string, string][]);
    return (await this.call<{ audit: AuditRow[] }>("GET", `/v1/audit${p.size ? `?${p}` : ""}`)).audit;
  };
  settings = () => this.call<BankSettings>("GET", "/v1/settings");
  items = async (q: { mine?: boolean; status?: string; family?: string } = {}) => {
    const p = new URLSearchParams();
    if (q.mine) p.set("mine", "1");
    if (q.status) p.set("status", q.status);
    if (q.family) p.set("family", q.family);
    return this.call<{ items: BankItem[]; terms: string }>("GET", `/v1/items${p.size ? `?${p}` : ""}`);
  };
  item = (id: string) => this.call<BankItem>("GET", `/v1/items/${encodeURIComponent(id)}`);
  /** A new item, or `item`'s next version, its kit uploaded: the terms agreed. */
  submit = async (kit: Blob, note: string, item?: string) => {
    const made = await this.call<{ id: string; version: number; upload_url: string }>(
      "POST",
      item ? `/v1/items/${encodeURIComponent(item)}/versions` : "/v1/items",
      { note, terms: true },
    );
    const tok = await this.token();
    const res = await this.fetcher(made.upload_url, {
      method: "PUT",
      headers: { Authorization: `Bearer ${tok.token}`, "Content-Type": "application/gzip" },
      body: kit,
    });
    return read<{ id: string; version: number; state: VersionState }>(res);
  };
  review = (id: string, version: number, verdict: "accept" | "reject" | "changes", comment: string) =>
    this.call<BankItem>("POST", `/v1/items/${encodeURIComponent(id)}/versions/${version}/review`, { verdict, comment });
  setStatus = (id: string, status: "calibrated" | "active" | "retired", why = "") =>
    this.call<BankItem>("POST", `/v1/items/${encodeURIComponent(id)}/status`, { status, why });
  withdraw = (id: string) => this.call<void>("POST", `/v1/items/${encodeURIComponent(id)}/withdraw`);
  deleteItem = (id: string) => this.call<void>("DELETE", `/v1/items/${encodeURIComponent(id)}`);
  /** A version's part as a file: the hidden part's reading is an audit row. */
  kit = async (id: string, version: number, part: "public" | "hidden") => {
    const tok = await this.token();
    const res = await this.fetcher(`${tok.url}/v1/items/${encodeURIComponent(id)}/versions/${version}/kit?part=${part}`, {
      headers: { Authorization: `Bearer ${tok.token}` },
    });
    if (!res.ok) await read(res);
    return res.blob();
  };
  jobs = async (q: { state?: string; target?: string } = {}) => {
    const p = new URLSearchParams(Object.entries(q).filter(([, v]) => v) as [string, string][]);
    return (await this.call<{ jobs: BankJob[] }>("GET", `/v1/jobs${p.size ? `?${p}` : ""}`)).jobs;
  };
  retryJob = (id: string) => this.call<BankJob>("POST", `/v1/jobs/${encodeURIComponent(id)}/retry`);
  blueprints = async () => (await this.call<{ blueprints: Blueprint[] }>("GET", "/v1/blueprints")).blueprints;
  blueprint = (id: string, version?: number) =>
    this.call<Blueprint>("GET", `/v1/blueprints/${encodeURIComponent(id)}${version ? `?version=${version}` : ""}`);
  createBlueprint = (edn: string) => this.call<Blueprint>("POST", "/v1/blueprints", { edn });
  newBlueprintVersion = (id: string, edn: string) => this.call<Blueprint>("POST", `/v1/blueprints/${encodeURIComponent(id)}/versions`, { edn });
  publishBlueprint = (id: string, version: number) => this.call<Blueprint>("POST", `/v1/blueprints/${encodeURIComponent(id)}/publish`, { version });
  trial = (id: string, klass?: string) => this.call<{ job: string }>("POST", `/v1/blueprints/${encodeURIComponent(id)}/trial`, klass ? { class: klass } : {});
  trialResult = (job: string) =>
    this.call<{ state: string; output: { drew: boolean; report: string } | null; error: string }>("GET", `/v1/trials/${encodeURIComponent(job)}`);
  requestPaper = (r: { blueprint: string; class: string; opens_at?: string; closes_at?: string; forms?: number; note?: string }) =>
    this.call<PaperRequest>("POST", "/v1/paper-requests", r);
  requests = async (all = false) => (await this.call<{ requests: PaperRequest[] }>("GET", `/v1/paper-requests${all ? "?all=1" : ""}`)).requests;
  paperRequest = (id: string) => this.call<{ request: PaperRequest; papers: Paper[] }>("GET", `/v1/paper-requests/${encodeURIComponent(id)}`);
  decide = (id: string, approve: boolean, why: string) =>
    this.call<PaperRequest>("POST", `/v1/paper-requests/${encodeURIComponent(id)}/decision`, { approve, why });
  withdrawRequest = (id: string) => this.call<void>("POST", `/v1/paper-requests/${encodeURIComponent(id)}/withdraw`);
  paperGrading = (id: string) => this.call<PaperGrading>("GET", `/v1/papers/${encodeURIComponent(id)}/grading`);
  regrade = (id: string, submission: string, reason: string) =>
    this.call<{ job: string }>("POST", `/v1/papers/${encodeURIComponent(id)}/regrade`, { submission, reason });
  papers = async (all = false) => (await this.call<{ papers: Paper[] }>("GET", `/v1/papers${all ? "?all=1" : ""}`)).papers;
  paper = (id: string) => this.call<Paper>("GET", `/v1/papers/${encodeURIComponent(id)}`);
  setSettings = (s: Partial<Omit<BankSettings, "updated_by" | "updated_at">>) => this.call<BankSettings>("PATCH", "/v1/settings", s);
}

async function read<T>(res: Response): Promise<T> {
  const text = await res.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = undefined;
  }
  if (!res.ok) {
    const what = word("errors:status", String(res.status)) ?? t("errors:status.other");
    const msg =
      data && typeof data === "object" && typeof (data as { error?: unknown }).error === "string"
        ? t("errors:relayError", { what, detail: (data as { error: string }).error })
        : t("errors:noAnswer", { status: res.status, statusText: res.statusText });
    throw new ApiError(res.status, msg, data);
  }
  return data as T;
}

/** The app's one client: one token for every page. */
export const bank = new BankClient();
